import * as React from "react";
import { connect } from "react-redux";
import Tooltip from "../../../Tooltip";
import * as rt from "../../../../lib/SimulationReportModels";
import { DraggableRect } from "../Draggable/DraggableElements";
import { INSIGHT_TYPES } from "../../../../lib/ManufacturingTags.generated";
import { PropertiesLogic } from "../reducers/BusinessLogic";
import * as r from "../../../../lib/backend/reports.generated.types";
import OrderDisabledShifts from "./OrderDisabledShifts";
import { ValidLinesOrdersHelper } from "../reducers/ValidLinesOrdersHelper";

import type { IsInsideOrderAction, TranslateCursorAction } from "../Draggable/reducers";
import type { GanttChartSourceTypes } from "../reducers/properties";
import type { ReduxState } from "../reducers/reducers";
import type { IOrderProducedModelEx } from "../reducers/linesOrders";

export type OrderRectangleAttributes = {
    x: number;
    y: number;
    rx: number;
    ry: number;
    width: number;
    height: number;
    order: rt.ISimulationReportOrderEx;
    unscheduled_order?: IOrderProducedModelEx;
    fill: string;
    insight_tooltips: React.ReactNode[];
    insight_hash: string;
    has_insight: boolean;
    order_uuid: string;
    line_uuid: string;
    source_type: GanttChartSourceTypes;
    is_extra_line: boolean;
    is_filter_highlighted: boolean;
    production_version: string;
    history_x: number | null;
    fill_opacity?: number;
    exceeds_deadline: boolean;
    priority: string;
};

export type OwnProps = OrderRectangleAttributes & {
    tooltip?: React.ReactNode;
    unscheduled_highlighted?: boolean;
    insight_freeze_order: boolean;
    is_inside_order: boolean;
    earliest_end: number;
    shift_number: number;
    contextDispatch: (args: IsInsideOrderAction | TranslateCursorAction) => void;
    order_index: number;
    line_index: number;
    earliest_start: number;
    is_multiselected: boolean;
    is_unscheduled: boolean;
    is_extra_line: boolean;
    disabled_shifts: r.ISimulationReportOrderLineShiftFeatureItem[];
    material_title: string;
    cursor: string;
};

type Props = OwnProps & ReduxProps;

class OrderRectangle extends React.Component<Props> {
    getStrokeAttributes = () => {
        let stroke = "#616e7c";
        if (!this.props.is_rescheduled && this.props.is_highlighted) {
            if (this.props.highlighted_type === "input") {
                stroke = "#f5a623";
            } else if (this.props.highlighted_type === "order") {
                stroke = "#2186eb";
            } else if (this.props.highlighted_type === "output") {
                stroke = "#e13131";
            } else {
                stroke = "#e13131";
            }
        }

        const fill = this.props.fill;
        let stroke_width =
            this.props.is_rescheduled ||
            this.props.is_highlighted ||
            this.props.is_filter_highlighted ||
            this.props.is_highlighted_move_order ||
            this.props.is_multiselected
                ? 5
                : 1;
        let stroke_dash_array: string | undefined = undefined;

        if (this.props.insight_freeze_order && this.props.is_freeze_selected_dropdown) {
            stroke = this.props.has_frozen_insight && this.props.is_freeze_selected_dropdown ? "red" : "blue";
            stroke_width = this.props.is_rescheduled || this.props.is_highlighted ? 6 : 3;
            stroke_dash_array = "5 11";
        }

        if (this.props.has_partially_scheduled_insight && this.props.is_partially_scheduled_selected_dropdown) {
            stroke_width = this.props.is_rescheduled || this.props.is_highlighted ? 6 : 4;
            stroke_dash_array = "5 5";
        }

        // if the order is highlighted, we already handled color at the start of the function
        if (this.props.is_unscheduled && !this.props.is_highlighted) {
            stroke = "#FD972B";
        }

        if (this.props.is_highlighted_move_order) {
            stroke = "#2186eb";
        }

        if (this.props.is_rescheduled) {
            stroke = "black";
        }

        if (this.props.is_multiselected) {
            stroke = "#0062cc";
            stroke_width = 2.5;
        }

        if (
            this.props.exceeds_deadline &&
            this.props.is_exceeds_deadline_selected_dropdown &&
            !this.props.is_highlighted &&
            !this.props.is_rescheduled &&
            !this.props.is_highlighted_move_order &&
            !this.props.is_multiselected
        ) {
            if (this.props.priority === "A") {
                stroke = "#dc3545";
            } else {
                stroke = "#f79e02";
            }
            stroke_width = 2;
        }

        return {
            stroke,
            fill,
            strokeWidth: stroke_width,
            strokeDasharray: stroke_dash_array,
        };
    };

    splitIfHistory = () => {
        const history_x = this.props.history_x;
        if (PropertiesLogic.isPlanningTable(this.props.source_type) || !history_x) {
            return {
                nonghost_width: null,
                ghost_width: this.props.width,
            };
        }
        const ghost_width = history_x - this.props.x;
        const nonghost_width = this.props.width - ghost_width;
        return {
            nonghost_width,
            ghost_width: ghost_width,
        };
    };

    getPreferedStrokeSide = () => {
        if (this.props.reschedule_line_uuid) {
            const order_uuids = ValidLinesOrdersHelper.getPreferredOperationUuids(this.props.reschedule_line_uuid);
            if (order_uuids && order_uuids.includes(this.props.order_uuid)) {
                const { ghost_width: width } = this.splitIfHistory();
                const stroke_width = this.props.height / 2.5;
                const y = this.props.y + this.props.height - stroke_width / 2;
                const x1 = this.props.x;
                const y1 = y;
                const x2 = this.props.x + width;
                const y2 = y;
                return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFD700" strokeWidth={stroke_width}></line>;
            }
        }
        return null;
    };

    getOpacity(nonghost_width: number | null) {
        if (this.props.fill_opacity) {
            return this.props.fill_opacity;
        }

        if (this.props.is_unscheduled) {
            return 0.01;
        }

        return nonghost_width !== null ? 0.2 : 0.8;
    }

    render() {
        if (this.props.width < 0) {
            return null;
        }

        const { ghost_width: width, nonghost_width } = this.splitIfHistory();
        const stroke_attributes = this.getStrokeAttributes();
        const is_currently_active = this.props.order != null && this.props.order.is_currently_active === true;
        return (
            <g name="order-rectangle">
                <Tooltip
                    key={
                        this.props.order_uuid + "-" + this.props.is_rescheduled.toString() + "-" + this.props.line_uuid
                    }
                    content={this.props.tooltip}
                >
                    <DraggableRect
                        key={
                            this.props.order_uuid +
                            "-" +
                            this.props.is_rescheduled.toString() +
                            "-" +
                            this.props.line_uuid
                        }
                        name="order-rect"
                        cursor={this.props.cursor}
                        order-uuid={this.props.order_uuid}
                        x={this.props.x}
                        y={this.props.y}
                        rx={0}
                        ry={0}
                        fillOpacity={this.getOpacity(nonghost_width)}
                        width={width}
                        height={this.props.height}
                        line-uuid={this.props.line_uuid}
                        order_uuid={this.props.order_uuid}
                        earliest_end={this.props.earliest_end}
                        earliest-start={this.props.earliest_start}
                        line_uuid={this.props.line_uuid}
                        order_index={this.props.order_index}
                        shift_number={this.props.shift_number}
                        line_index={this.props.line_index}
                        source_type={this.props.source_type}
                        is_unscheduled={this.props.is_unscheduled}
                        is_rescheduled={this.props.is_rescheduled}
                        production_version={this.props.production_version}
                        is_extra_line={this.props.is_extra_line}
                        stroke={stroke_attributes.stroke}
                        fill={stroke_attributes.fill}
                        strokeWidth={stroke_attributes.strokeWidth}
                        strokeDasharray={stroke_attributes.strokeDasharray}
                        className={is_currently_active ? "active-order" : ""}
                    />
                    {this.getPreferedStrokeSide()}
                    {nonghost_width && (
                        // @ts-ignore - this.props contain attributes that are not present in the rect element
                        <rect
                            id="ghost-rect"
                            {...this.getStrokeAttributes()}
                            {...this.props}
                            fillOpacity={1}
                            x={this.props.history_x || 0}
                            width={nonghost_width}
                        />
                    )}
                    <OrderDisabledShifts
                        order_uuid={this.props.order_uuid}
                        disabled_shifts={this.props.disabled_shifts}
                        order_rect_stroke={stroke_attributes.strokeWidth}
                        y={this.props.y}
                        material_title={this.props.material_title}
                    />
                </Tooltip>
            </g>
        );
    }
}

const mapState = (state: ReduxState, own_props: OwnProps) => {
    const highlighted_orders = state.gantt_chart_planning_table.highlighted_orders;
    const planning_table = state.gantt_chart_planning_table;
    const insights_mapped = state.gantt_chart_insights.insights_mapped;
    const reschedule_line_uuid = state.gantt_chart_filters.reschedule_line_uuid;

    const filtered_insight_types = state.standalone_gantt.insight_types;
    const is_freeze_selected_dropdown = filtered_insight_types.includes(INSIGHT_TYPES.freeze_order_move);
    const is_partially_scheduled_selected_dropdown = filtered_insight_types.includes(INSIGHT_TYPES.partially_scheduled);
    const is_exceeds_deadline_selected_dropdown = filtered_insight_types.includes(
        INSIGHT_TYPES.operation_exceeds_deadline
    );

    const insights = insights_mapped == null ? [] : insights_mapped.get(own_props.order_uuid) || [];
    const has_frozen_insight = insights.some(i => i.type === INSIGHT_TYPES.freeze_order_move);
    const has_partially_scheduled_insight = insights.some(i => i.type === INSIGHT_TYPES.partially_scheduled);

    let is_highlighted = false;
    let highlighted_type: string | undefined = undefined;
    if (highlighted_orders.highlighted_orders_map != null) {
        const highlighted_order = highlighted_orders.highlighted_orders_map.get(own_props.order_uuid);
        if (highlighted_order != null) {
            is_highlighted = highlighted_order.is_highlighted;
            highlighted_type = highlighted_order.type;
        }
    }

    const is_highlighted_move_order = planning_table.highlighted_move_order_uuid === own_props.order_uuid;

    return {
        is_rescheduled: planning_table.last_clicked_order_uuid === own_props.order_uuid,
        is_ctrl_down: planning_table.is_ctrl_down,
        is_highlighted,
        highlighted_type,
        is_highlighted_move_order,
        is_freeze_selected_dropdown,
        has_frozen_insight,
        is_partially_scheduled_selected_dropdown,
        has_partially_scheduled_insight,
        reschedule_line_uuid,
        is_exceeds_deadline_selected_dropdown,
    };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReturnType<typeof mapState>, {}, OwnProps, ReduxState>(mapState)(OrderRectangle);
